import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import type { components } from "@dolfin/api-admin";

type OutstandingPayablesResponse = components["schemas"]["OutstandingPayablesResponse"];
type OverduePayablesResponse = components["schemas"]["OverduePayablesResponse"];
type PaidResponse = components["schemas"]["PaidResponse"];

export interface ApStats {
  loading: boolean;
  error: string | null;
  outstanding: OutstandingPayablesResponse | null;
  overdue: OverduePayablesResponse | null;
  paid30: PaidResponse | null;
  paid90: PaidResponse | null;
  refresh: () => Promise<void>;
}

/** AP overview aggregates, the AP mirror of useArStats. paid90 backs Top suppliers (90 days, as on AR). */
export function useApStats(): ApStats {
  const { activeOrganisationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [outstanding, setOutstanding] = useState<OutstandingPayablesResponse | null>(null);
  const [overdue, setOverdue] = useState<OverduePayablesResponse | null>(null);
  const [paid30, setPaid30] = useState<PaidResponse | null>(null);
  const [paid90, setPaid90] = useState<PaidResponse | null>(null);

  const fetchStats = useCallback(async () => {
    if (!activeOrganisationId) return;
    setLoading(true);
    setError(null);

    try {
      const [outstandingRes, overdueRes, paid30Res, paid90Res] = await Promise.all([
        api.GET("/v1/payables/outstanding", {}),
        api.GET("/v1/payables/overdue", {}),
        api.GET("/v1/payables/paid", { params: { query: { period: "Last30Days" } } }),
        api.GET("/v1/payables/paid", { params: { query: { period: "Last90Days" } } }),
      ]);

      if (outstandingRes.error || overdueRes.error || paid30Res.error || paid90Res.error) {
        setError("Failed to load payables stats");
      }

      setOutstanding(outstandingRes.data ?? null);
      setOverdue(overdueRes.data ?? null);
      setPaid30(paid30Res.data ?? null);
      setPaid90(paid90Res.data ?? null);
    } catch (e) {
      console.error("Failed to load payables stats:", e);
      setError("Failed to load payables stats");
    } finally {
      setLoading(false);
    }
  }, [activeOrganisationId]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  return { loading, error, outstanding, overdue, paid30, paid90, refresh: fetchStats };
}
